/**
 * Récolte, métiers et économie (GDD §5 et §7). Module pur.
 *
 * Symétrie voulue avec le combat : le combat est verrouillé par la puissance,
 * la récolte par le **niveau de métier**. Le craft, lui, n'a aucun verrou :
 * seuls les matériaux comptent.
 *
 * Les niveaux de métier sont **par personnage** : c'est ce qui rendra un
 * deuxième personnage désirable (GDD §9). Un même personnage ne peut pas être
 * paysan 200 et mineur 200.
 */
import type { Personnage } from "./etat.ts"
import type { Contenu, Item, Recolte } from "./contenu.ts"

export const NIVEAU_METIER_MAX = 200

/** Niveau atteint dans un métier, 1 par défaut. */
export const niveauMetier = (p: Personnage, metier: number) => p.metiers[metier]?.niveau ?? 1

export const xpMetier = (p: Personnage, metier: number) => p.metiers[metier]?.xp ?? 0

/** Le niveau de métier est le verrou d'accès aux ressources (GDD §5). */
export const accessible = (p: Personnage, r: Recolte) =>
  niveauMetier(p, r.metier) >= r.niveauRequis

/**
 * Durée d'une récolte. Une ressource plus haute est plus lente ; monter le
 * métier accélère l'ensemble, ce qui rend la progression sensible même quand on
 * reste sur la même ressource.
 */
export function tempsRecolte(r: Recolte, niveauJoueur: number): number {
  const base = 3 + r.niveauRequis * 0.05
  return base / (1 + niveauJoueur / 150)
}

/** XP de métier par récolte. */
export const xpParRecolte = (r: Recolte) => 10 + r.niveauRequis * 2

/** XP nécessaire pour passer au niveau de métier suivant. */
export const xpRequiseMetier = (n: number) => Math.round(20 * Math.pow(n, 1.5))

/**
 * Prix de rachat par un PNJ, **seule source de kamas du jeu** (GDD §7).
 *
 * Conséquence recherchée : vendre et crafter puisent dans le même stock, donc
 * chaque session pose un arbitrage. C'est la décision économique permanente,
 * obtenue sans rien coder de plus.
 *
 * Exposant 1,1 et non 1,5 : la cadence de récolte étant à peu près constante,
 * un exposant élevé fait croître le revenu horaire en niveau^1,5. Mesuré, cela
 * donnait 8,5 M de kamas en 12 h, de quoi acheter toute l'échelle des
 * personnages en une journée. Un prix quasi linéaire garde le haut niveau
 * meilleur sans le rendre absurde.
 */
export const prixVente = (it: Item) => Math.max(1, Math.round(Math.pow(it.niveau, 1.1) * 1.5))

/** Ce que rapporterait la vente de tout un lot. */
export function valeurLot(c: Contenu, inventaire: Record<number, number>): number {
  let total = 0
  for (const [id, n] of Object.entries(inventaire)) {
    const it = c.items[id]
    if (it) total += prixVente(it) * n
  }
  return total
}
